import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, TrendingUp, BarChart3, Cog, Database, Megaphone, ArrowRight, Copy, Check, Calendar } from "lucide-react";
import { useState } from "react";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/components/ThemeContext";

const CONSULTATION_URL = "https://marketing.redhotzone.org/widget/booking/Ntxsco4meo4LLdDTXmop";

const pillarIcons = {
  "Strategic Direction": Target,
  "Marketing System": Megaphone,
  "Sales System": TrendingUp,
  "Operations System": Cog,
  "Systems & Automation": Database,
  "Data & Reporting": BarChart3,
};

const ratingConfig = {
  "Optimised": { color: "#94CE77", bg: "rgba(148,206,119,0.1)", border: "rgba(148,206,119,0.2)" },
  "Advanced": { color: "#9CCEFF", bg: "rgba(156,206,255,0.1)", border: "rgba(156,206,255,0.2)" },
  "Structured": { color: "#02808F", bg: "rgba(2,128,143,0.1)", border: "rgba(2,128,143,0.2)" },
  "Developing": { color: "#FDB714", bg: "rgba(253,183,20,0.1)", border: "rgba(253,183,20,0.2)" },
  "Poor": { color: "#DC0706", bg: "rgba(220,7,6,0.1)", border: "rgba(220,7,6,0.2)" },
};

function ScoreBar({ score, maxScore = 10 }) {
  const pct = (score / maxScore) * 100;
  const color = score >= 9 ? "#94CE77" : score >= 7 ? "#9CCEFF" : score >= 5 ? "#02808F" : score >= 3 ? "#FDB714" : "#DC0706";
  return (
    <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--rhz-border)' }}>
      <motion.div
        className="h-full rounded-full"
        style={{ backgroundColor: color }}
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
    </div>
  );
}

export default function ReportView({ audit }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const { logos, logoStyle } = useTheme();
  const { scores, report, business_name, first_name } = audit;

  const copyLink = () => {
    try {
      const url = window.location.href;
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }).catch(() => fallbackCopy(url));
      } else {
        fallbackCopy(url);
      }
    } catch { fallbackCopy(window.location.href); }
  };

  const fallbackCopy = (text) => {
    const el = document.createElement("textarea");
    el.value = text;
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const pillarEntries = scores?.pillar_scores ? Object.entries(scores.pillar_scores) : [];
  const sortedPillars = [...pillarEntries].sort((a, b) => a[1].score - b[1].score);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rhz-bg)', color: 'var(--rhz-text-primary)' }} data-testid="report-view">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl" style={{ backgroundColor: 'var(--rhz-nav-bg)', borderBottom: '1px solid var(--rhz-nav-border)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <img src={logos.horizontal} alt="RHZ" className="h-6 sm:h-8" style={logoStyle} />
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <button
              data-testid="copy-link-btn"
              onClick={copyLink}
              className="hidden sm:flex items-center gap-2 transition-colors font-['Albert_Sans'] text-xs uppercase tracking-wider"
              style={{ color: 'var(--rhz-text-secondary)' }}
            >
              {copied ? <Check className="h-4 w-4 text-[#94CE77]" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy Link"}
            </button>
            <button
              data-testid="copy-link-btn-mobile"
              onClick={copyLink}
              className="sm:hidden p-2 rounded-sm transition-colors"
              style={{ color: 'var(--rhz-text-secondary)', border: '1px solid var(--rhz-border)' }}
            >
              {copied ? <Check className="h-4 w-4 text-[#94CE77]" /> : <Copy className="h-4 w-4" />}
            </button>
            <a
              data-testid="nav-book-session-btn"
              href={CONSULTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#DC0706] text-white hover:bg-[#FE4D00] transition-colors px-3 sm:px-5 py-2 uppercase tracking-[0.15em] font-['Bruno_Ace_SC'] text-[10px] sm:text-xs rounded-sm"
            >
              <span className="hidden sm:inline">Book Free Session</span>
              <span className="sm:hidden">Book</span>
            </a>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Report Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10 sm:mb-16">
          <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.4em] uppercase text-[#DC0706] mb-4">Growth Audit Report</p>
          <h1
            className="font-['Bruno_Ace_SC'] text-3xl sm:text-4xl lg:text-5xl tracking-[0.15em] uppercase mb-4"
            style={{ color: 'var(--rhz-text-primary)' }}
            data-testid="report-title"
          >
            {business_name || "Business"} Audit
          </h1>
          <p className="font-['Albert_Sans'] text-sm" style={{ color: 'var(--rhz-text-secondary)' }}>
            Prepared for {first_name || "Owner"} &mdash; {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </motion.div>

        {/* Overall Score */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="mb-10 sm:mb-16 p-5 sm:p-8 rounded-sm"
          style={{ backgroundColor: 'var(--rhz-surface)', border: '1px solid var(--rhz-border)', boxShadow: 'var(--rhz-card-shadow)' }}
          data-testid="overall-score-card"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <p className="font-['Bruno_Ace_SC'] text-xs uppercase tracking-[0.3em] mb-2" style={{ color: 'var(--rhz-text-secondary)' }}>Overall Maturity</p>
              <div className="flex items-baseline gap-3">
                <span className="font-['Bruno_Ace_SC'] text-4xl sm:text-5xl md:text-6xl text-flame" data-testid="overall-score">{scores?.overall_score || 0}</span>
                <span className="font-['Albert_Sans'] text-xl" style={{ color: 'var(--rhz-text-disabled)' }}>/ 10</span>
              </div>
            </div>
            <span
              className="inline-block px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider font-['Albert_Sans']"
              style={{
                backgroundColor: ratingConfig[scores?.overall_rating]?.bg || ratingConfig.Poor.bg,
                color: ratingConfig[scores?.overall_rating]?.color || ratingConfig.Poor.color,
                border: `1px solid ${ratingConfig[scores?.overall_rating]?.border || ratingConfig.Poor.border}`
              }}
              data-testid="overall-rating"
            >
              {scores?.overall_rating || "N/A"}
            </span>
          </div>
        </motion.div>

        {/* Executive Summary */}
        {report?.executive_summary && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-10 sm:mb-16" data-testid="executive-summary">
            <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.4em] uppercase text-[#DC0706] mb-4">Executive Summary</p>
            <div className="p-5 sm:p-8 rounded-sm" style={{ backgroundColor: 'var(--rhz-surface)', border: '1px solid var(--rhz-border)', boxShadow: 'var(--rhz-card-shadow)' }}>
              <p className="font-['Albert_Sans'] leading-relaxed text-base whitespace-pre-line" style={{ color: 'var(--rhz-text-secondary)' }}>
                {report.executive_summary}
              </p>
            </div>
          </motion.div>
        )}

        {/* Scorecard */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-10 sm:mb-16" data-testid="scorecard-section">
          <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.4em] uppercase text-[#DC0706] mb-4">Pillar Scorecard</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sortedPillars.map(([pillar, data], i) => {
              const Icon = pillarIcons[pillar] || Target;
              const cfg = ratingConfig[data.rating] || ratingConfig.Poor;
              return (
                <motion.div
                  key={pillar}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.08 }}
                  className="p-6 rounded-sm"
                  style={{ backgroundColor: 'var(--rhz-surface)', border: '1px solid var(--rhz-border)', boxShadow: 'var(--rhz-card-shadow)' }}
                  data-testid={`pillar-score-${pillar.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5" style={{ color: cfg.color }} />
                      <span className="font-['Albert_Sans'] font-bold text-sm uppercase tracking-wider" style={{ color: 'var(--rhz-text-primary)' }}>{pillar}</span>
                    </div>
                    <span
                      className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider font-['Albert_Sans']"
                      style={{ backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}
                    >
                      {data.rating}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <ScoreBar score={data.score} />
                    <span className="font-['Bruno_Ace_SC'] text-lg min-w-[3rem] text-right" style={{ color: 'var(--rhz-text-primary)' }}>{data.score}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Key Findings */}
        {report?.key_findings?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mb-10 sm:mb-16" data-testid="key-findings-section">
            <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.4em] uppercase text-[#DC0706] mb-4">Key Findings</p>
            <div className="space-y-6">
              {report.key_findings.map((finding, i) => {
                const cfg = ratingConfig[scores?.pillar_scores?.[finding.pillar]?.rating] || ratingConfig.Poor;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.1 }}
                    className="rounded-sm overflow-hidden"
                    style={{ backgroundColor: 'var(--rhz-surface)', border: '1px solid var(--rhz-border)', boxShadow: 'var(--rhz-card-shadow)' }}
                    data-testid={`finding-${i}`}
                  >
                    <div className="p-6" style={{ borderBottom: '1px solid var(--rhz-border)' }}>
                      <span className="text-xs font-bold uppercase tracking-wider font-['Albert_Sans']" style={{ color: cfg.color }}>{finding.pillar}</span>
                    </div>
                    <div className="p-6 space-y-6">
                      <div>
                        <p className="font-['Albert_Sans'] text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--rhz-text-disabled)' }}>Observation</p>
                        <p className="font-['Albert_Sans'] text-sm leading-relaxed" style={{ color: 'var(--rhz-text-primary)' }}>{finding.observation}</p>
                      </div>
                      <div>
                        <p className="font-['Albert_Sans'] text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--rhz-text-disabled)' }}>Why This Is a Problem</p>
                        <p className="font-['Albert_Sans'] text-sm leading-relaxed" style={{ color: 'var(--rhz-text-secondary)' }}>{finding.why_problem}</p>
                      </div>
                      <div className="p-4 border-l-2 border-[#02808F] rounded-sm" style={{ backgroundColor: 'var(--rhz-bg)' }}>
                        <p className="font-['Albert_Sans'] text-xs uppercase tracking-wider text-[#02808F] mb-2">Industry Norm</p>
                        <p className="font-['Albert_Sans'] text-sm leading-relaxed" style={{ color: 'var(--rhz-text-secondary)' }}>{finding.industry_norm}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Growth Constraints */}
        {report?.growth_constraints?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mb-10 sm:mb-16" data-testid="growth-constraints-section">
            <p className="font-['Bruno_Ace_SC'] text-xs tracking-[0.4em] uppercase text-[#DC0706] mb-4">Top Growth Constraints</p>
            <div className="space-y-4">
              {report.growth_constraints.map((constraint, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 + i * 0.1 }}
                  className="flex gap-5 p-6 rounded-sm"
                  style={{ backgroundColor: 'var(--rhz-surface)', border: '1px solid var(--rhz-border)', boxShadow: 'var(--rhz-card-shadow)' }}
                  data-testid={`constraint-${i}`}
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-[#DC0706]/10 border border-[#DC0706]/20 flex items-center justify-center">
                      <span className="font-['Bruno_Ace_SC'] text-sm text-[#DC0706]">{constraint.rank || i + 1}</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-['Albert_Sans'] font-bold text-sm uppercase tracking-wider mb-2" style={{ color: 'var(--rhz-text-primary)' }}>
                      {constraint.title}
                    </h4>
                    <p className="font-['Albert_Sans'] text-sm leading-relaxed" style={{ color: 'var(--rhz-text-secondary)' }}>
                      {constraint.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Free Consultation CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          className="mb-10 sm:mb-16 p-6 sm:p-10 rounded-sm text-center relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(220,7,6,0.08), rgba(254,77,0,0.08), rgba(253,183,20,0.05))',
            border: '1px solid rgba(220,7,6,0.2)',
          }}
          data-testid="consultation-cta"
        >
          <div className="relative z-10">
            <div className="w-14 h-14 rounded-full bg-[#DC0706]/10 border border-[#DC0706]/20 flex items-center justify-center mx-auto mb-6">
              <Calendar className="h-6 w-6 text-[#DC0706]" />
            </div>
            <h3 className="font-['Bruno_Ace_SC'] text-xl sm:text-2xl uppercase tracking-[0.15em] mb-4" style={{ color: 'var(--rhz-text-primary)' }}>
              Free Growth Consultation
            </h3>
            <p className="font-['Albert_Sans'] text-sm max-w-lg mx-auto mb-8 leading-relaxed" style={{ color: 'var(--rhz-text-secondary)' }}>
              Ready to address these findings? Book a complimentary 1-hour growth consultation session to discuss your audit results with our team.
            </p>
            <a
              data-testid="book-consultation-btn"
              href={CONSULTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#DC0706] text-white hover:bg-[#FE4D00] transition-all px-10 py-4 uppercase tracking-[0.25em] font-['Bruno_Ace_SC'] text-sm rounded-sm glow-red"
            >
              Book Free Session
              <ArrowRight className="h-5 w-5" />
            </a>
            <p className="font-['Albert_Sans'] text-xs mt-4 uppercase tracking-wider" style={{ color: 'var(--rhz-text-disabled)' }}>
              No obligation &mdash; 1 hour &mdash; Online
            </p>
          </div>
        </motion.div>

        {/* Bottom actions */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
          className="text-center py-10 sm:py-16" style={{ borderTop: '1px solid var(--rhz-border)' }}
        >
          <p className="font-['Albert_Sans'] text-xs sm:text-sm uppercase tracking-wider mb-4" style={{ color: 'var(--rhz-text-disabled)' }}>
            This report is a diagnostic assessment only
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-4 sm:px-0">
            <button
              data-testid="copy-report-link-btn"
              onClick={copyLink}
              className="flex items-center gap-2 px-6 py-3 rounded-sm transition-all font-['Albert_Sans'] text-xs uppercase tracking-wider"
              style={{ border: '1px solid var(--rhz-border)', color: 'var(--rhz-text-secondary)' }}
            >
              {copied ? <Check className="h-4 w-4 text-[#94CE77]" /> : <Copy className="h-4 w-4" />}
              {copied ? "Link Copied" : "Save Report Link"}
            </button>
            <button
              data-testid="start-new-audit-btn"
              onClick={() => navigate("/")}
              className="flex items-center gap-2 bg-[#DC0706] text-white hover:bg-[#FE4D00] transition-colors px-6 py-3 uppercase tracking-[0.15em] font-['Bruno_Ace_SC'] text-xs rounded-sm"
            >
              New Audit
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </motion.div>

        {/* Footer */}
        <div className="py-6 flex items-center justify-between" style={{ borderTop: '1px solid var(--rhz-border)' }}>
          <img src={logos.horizontal} alt="RHZ" className="h-6 opacity-40" style={logoStyle} />
          <p className="font-['Albert_Sans'] text-xs" style={{ color: 'var(--rhz-text-disabled)' }}>Red Hot Zone Growth Audit</p>
        </div>
      </div>
    </div>
  );
}
